import { Box, Stack, Typography } from "@mui/material";

// The kit's own, so the `basis` on every figure in the product can be
// reached without a mouse. MUI's opens on hover and on focus, and the
// figure it wraps is a `Typography`, which takes neither.
import Tooltip from "./Tooltip.jsx";

/**
 * The one figure a screen is named after.
 *
 * ## Why this exists
 *
 * Every screen in this product answers a question — *how much of this
 * happened without a person*, *what has this agent cost*, *what is in the
 * graph* — and until there was a `display` size in the scale, the answer was
 * set two points larger than the caption under it. A console where nothing
 * can be emphatic is a console where everything is equally ignorable.
 *
 * So: one of these per screen, holding the number that screen exists to
 * produce. **A second on the same page is two headlines and therefore none**,
 * which is the only rule about using it that matters.
 *
 * ## It still has to be honest
 *
 * A big number is a claim, and the bigger it is set the more it claims. This
 * is the same argument `Stat` makes about its label and it matters more here,
 * because three points of type size are three points of confidence.
 *
 * `caption` is what the figure is *of* and it is not optional — `94%` alone
 * is a number about nothing, `94%` above *"had nobody behind them"* is a
 * fact. `basis` is the longer sentence: what was counted, over what window,
 * and what that does not prove. It hangs on the figure rather than beside it,
 * because a person reading a headline is already looking at the headline.
 *
 * ## The rule on the left
 *
 * Two pixels of brand colour down the edge. It is the one place in this
 * product where the accent appears without carrying a meaning — semantic
 * colour is spoken for by permitted, refused, attention and unattended, and
 * this deliberately is not one of them. It marks *this is the answer*, which
 * is a position on the page rather than a state of the data.
 */
export default function Headline({
  eyebrow,
  figure,
  caption,
  basis,
  lede,
  tone,
  children,
  right,
}) {
  const number = (
    <Typography
      variant="display"
      component="div"
      sx={{ color: tone ? `kernel.${tone}` : "text.primary" }}
    >
      {figure}
    </Typography>
  );

  return (
    <Box
      sx={{
        display: "flex",
        gap: { xs: 2, md: 4 },
        flexWrap: "wrap",
        alignItems: "flex-start",
        justifyContent: "space-between",
        minWidth: 0,
        pl: { xs: 2, sm: 2.5 },
        py: 0.5,
        borderLeft: 2,
        borderColor: "primary.main",
      }}
    >
      <Box sx={{ minWidth: 0, flex: "1 1 260px" }}>
        {eyebrow ? (
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ display: "block", mb: 0.25 }}
          >
            {eyebrow}
          </Typography>
        ) : null}

        {/* The tooltip is on the figure and not on a question mark beside it.
            Somebody who wants to know what a number is of is already pointing
            at the number. */}
        {basis ? (
          <Tooltip title={basis}>
            <Box sx={{ display: "inline-block", cursor: "help" }}>{number}</Box>
          </Tooltip>
        ) : (
          number
        )}

        {caption ? (
          <Typography
            variant="lede"
            component="div"
            sx={{ mt: 0.25, maxWidth: "46ch" }}
          >
            {caption}
          </Typography>
        ) : null}

        {lede ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 1, maxWidth: "62ch" }}
          >
            {lede}
          </Typography>
        ) : null}

        {children}
      </Box>

      {right ? (
        <Box sx={{ minWidth: 0, flex: "0 1 auto", alignSelf: "center" }}>
          {right}
        </Box>
      ) : null}
    </Box>
  );
}

/**
 * A row of figures under a headline, for the screens where the answer is
 * genuinely several numbers rather than one.
 *
 * Deliberately a different component from `Stats`, which is the small
 * label-carries-the-basis row inside a card. These are set at `h1` rather
 * than `display`: big enough to lead a screen, small enough that four of them
 * do not read as four headlines.
 */
export function Figures({ children, sx }) {
  return (
    <Stack
      direction="row"
      sx={{
        flexWrap: "wrap",
        columnGap: { xs: 3, sm: 5 },
        rowGap: 2,
        minWidth: 0,
        ...sx,
      }}
    >
      {children}
    </Stack>
  );
}

/** One of them. `label` carries the basis, exactly as `Stat` does. */
export function Figure({ value, label, basis, tone, href, onClick }) {
  const body = (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        component="div"
        sx={{
          fontSize: "1.75rem",
          fontWeight: 300,
          lineHeight: 1.1,
          letterSpacing: "-0.02em",
          fontVariantNumeric: "tabular-nums",
          color: tone ? `kernel.${tone}` : "text.primary",
        }}
      >
        {value}
      </Typography>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{
          display: "inline-block",
          lineHeight: 1.5,
          borderBottom: basis ? "1px dotted" : undefined,
          borderColor: basis ? "divider" : undefined,
          cursor: basis ? "help" : undefined,
        }}
      >
        {label}
      </Typography>
    </Box>
  );

  const figure = basis ? <Tooltip title={basis}>{body}</Tooltip> : body;

  if (!onClick && !href) return figure;

  return (
    <Box
      component={href ? "a" : "button"}
      href={href}
      type={href ? undefined : "button"}
      onClick={onClick}
      sx={{
        p: 0,
        border: 0,
        background: "none",
        font: "inherit",
        color: "inherit",
        textAlign: "left",
        textDecoration: "none",
        cursor: "pointer",
        "&:hover": { color: "primary.main" },
      }}
    >
      {figure}
    </Box>
  );
}
